/**
 * Settings Page
 * src/pages/Settings.jsx
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Globe, RefreshCw, Save, DollarSign, Users, Copy, Check } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';
import { showToast } from '../components/Toast';
import { collection, getDocs, setDoc, doc, serverTimestamp } from 'firebase/firestore';

// Search Google Workspace directory for @cipheria.pro users
async function searchWorkspaceUsers(query) {
  const token = sessionStorage.getItem('gAccessToken');
  if (!token || query.length < 2) return [];
  try {
    const q = encodeURIComponent(query);
    const res = await fetch(
      `https://people.googleapis.com/v1/people:searchDirectoryPeople?query=${q}&readMask=emailAddresses,names&sources=DIRECTORY_SOURCE_TYPE_DOMAIN_PROFILE&pageSize=8`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!res.ok) return [];
    const data = await res.json();
    return (data.people || []).map(p => ({
      email: p.emailAddresses?.[0]?.value || '',
      name: p.names?.[0]?.displayName || '',
    })).filter(u => u.email);
  } catch {
    return [];
  }
}

const MODULES = [
  'dashboard','pagos','patrimonio','pasivos','ingresos',
  'gastos','proyeccion','fondos','vacaciones','metas','amex','catalogos','historial',
];

const MODULE_LABELS = {
  dashboard: 'Dashboard', pagos: 'Pagos', patrimonio: 'Patrimonio', pasivos: 'Pasivos',
  ingresos: 'Ingresos', gastos: 'Gastos', proyeccion: 'Proyección', fondos: 'Fondos',
  vacaciones: 'Vacaciones', metas: 'Metas', amex: 'AMEX', catalogos: 'Catálogos', historial: 'Historial',
};

const ROLE_COLORS = { Admin: '#10b981', Reader: '#6b7280', Collaborative: '#3b82f6' };

const DEFAULT_ADMINS = [
  { email: 'alberto@cipheria.pro', displayName: 'Alberto' },
  { email: 'paola@cipheria.pro',   displayName: 'Paola'   },
];

// Default permissions: simple boolean access per module (true = module visible in nav)
// Admin always has all, others start with full access (admin can restrict)
function defaultPermissions(role) {
  return MODULES.reduce((acc, m) => ({ ...acc, [m]: true }), {});
}

export default function Settings({ db, user }) {
  const { lang, rates, t, updateLang, updateRates } = useSettings();
  const [usdRate, setUsdRate] = useState(String(rates.USD || 20));
  const [eurRate, setEurRate] = useState(String(rates.EUR || 22));
  const [fetching, setFetching] = useState(false);

  // User management state
  const [usersData, setUsersData]   = useState([]);
  const [newEmail, setNewEmail]     = useState('');
  const [newRole, setNewRole]       = useState('Reader');
  const [savingUser, setSavingUser] = useState(false);
  const [copiedId, setCopiedId]     = useState(null);

  // Email autocomplete
  const [suggestions, setSuggestions] = useState([]);
  const [showSugg, setShowSugg]       = useState(false);
  const [suggLoading, setSuggLoading] = useState(false);
  const debounceRef = useRef(null);
  const inputRef    = useRef(null);

  // Load users + seed default admins
  useEffect(() => {
    if (!db) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'users'));
        const loaded = snap.docs.map(d => ({ id: d.id, ...d.data() }));

        // Seed default admins if missing
        const updated = [...loaded];
        for (const admin of DEFAULT_ADMINS) {
          if (!loaded.find(u => u.id === admin.email)) {
            const userData = {
              email: admin.email,
              displayName: admin.displayName,
              role: 'Admin',
              permissions: defaultPermissions('Admin'),
              createdAt: serverTimestamp(),
            };
            await setDoc(doc(db, 'users', admin.email), userData);
            updated.push({ id: admin.email, ...userData });
          }
        }
        setUsersData(updated);
      } catch (err) {
        console.error('Error loading users:', err);
      }
    })();
  }, [db]);

  const handleAddUser = async () => {
    if (!newEmail.trim()) return;
    setSavingUser(true);
    try {
      const email = newEmail.trim().toLowerCase();
      const userData = {
        email,
        displayName: email.split('@')[0],
        role: newRole,
        permissions: defaultPermissions(newRole),
        createdAt: serverTimestamp(),
      };
      await setDoc(doc(db, 'users', email), userData);
      setUsersData(prev => {
        const exists = prev.find(u => u.id === email);
        if (exists) return prev.map(u => u.id === email ? { ...u, ...userData, id: email } : u);
        return [...prev, { ...userData, id: email }];
      });
      setNewEmail('');
      showToast('Usuario guardado. Comparte el link de acceso.');
    } catch (err) {
      console.error('Error adding user:', err);
      showToast('Error al guardar usuario', 'error');
    } finally {
      setSavingUser(false);
    }
  };

  const handleUpdateRole = async (email, role) => {
    try {
      const perms = role === 'Admin'
        ? defaultPermissions('Admin')
        : (usersData.find(u => u.id === email)?.permissions || defaultPermissions(role));
      await setDoc(doc(db, 'users', email), { role, permissions: perms }, { merge: true });
      setUsersData(prev => prev.map(u => u.id === email ? { ...u, role, permissions: perms } : u));
      showToast('Rol actualizado');
    } catch (err) {
      showToast('Error al actualizar', 'error');
    }
  };

  const handleToggleAccess = async (email, module) => {
    const userData = usersData.find(u => u.id === email);
    if (!userData || userData.role === 'Admin') return;
    const perms = { ...(userData.permissions || defaultPermissions(userData.role)) };
    // Normalize legacy { read, write } format to boolean
    const current = typeof perms[module] === 'object' ? true : (perms[module] !== false);
    perms[module] = !current;
    try {
      await setDoc(doc(db, 'users', email), { permissions: perms }, { merge: true });
      setUsersData(prev => prev.map(u => u.id === email ? { ...u, permissions: perms } : u));
    } catch (err) {
      showToast('Error al actualizar permiso', 'error');
    }
  };

  // Debounced autocomplete: search Workspace + filter existing users
  const handleEmailChange = useCallback((val) => {
    setNewEmail(val);
    clearTimeout(debounceRef.current);
    if (val.length < 2) { setSuggestions([]); setShowSugg(false); return; }

    debounceRef.current = setTimeout(async () => {
      setSuggLoading(true);
      // Always filter existing registered users
      const local = usersData
        .filter(u => u.email.includes(val.toLowerCase()) || (u.displayName || '').toLowerCase().includes(val.toLowerCase()))
        .map(u => ({ email: u.email, name: u.displayName || u.email.split('@')[0], existing: true }));

      // Also query Workspace directory
      const remote = await searchWorkspaceUsers(val);
      const remoteFiltered = remote.filter(r => !local.find(l => l.email === r.email));

      const all = [...local, ...remoteFiltered];
      setSuggestions(all);
      setShowSugg(all.length > 0);
      setSuggLoading(false);
    }, 300);
  }, [usersData]);

  const handleSelectSugg = (sugg) => {
    setNewEmail(sugg.email);
    setSuggestions([]);
    setShowSugg(false);
    inputRef.current?.focus();
  };

  const handleCopyInvite = (email) => {
    const url = window.location.origin;
    const msg = `Has sido invitado a Takiro's Finances.\nInicia sesión con tu cuenta Google (${email}) en: ${url}`;
    navigator.clipboard.writeText(msg).then(() => {
      setCopiedId(email);
      showToast('Invitación copiada al portapapeles');
      setTimeout(() => setCopiedId(null), 2500);
    });
  };

  const fetchRates = async () => {
    setFetching(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch('https://api.frankfurter.app/latest?from=USD&to=MXN').then(r => r.json()),
        fetch('https://api.frankfurter.app/latest?from=EUR&to=MXN').then(r => r.json()),
      ]);
      const usd = r1.rates?.MXN;
      const eur = r2.rates?.MXN;
      if (usd) setUsdRate(usd.toFixed(2));
      if (eur) setEurRate(eur.toFixed(2));
      showToast(t('rateUpdated'));
    } catch {
      showToast(lang === 'en' ? 'Failed to fetch rates' : 'Error al obtener tipos de cambio', 'error');
    } finally {
      setFetching(false);
    }
  };

  const handleSaveRates = async () => {
    const newRates = { USD: parseFloat(usdRate) || 20, EUR: parseFloat(eurRate) || 22 };
    await updateRates(newRates);
    showToast(t('settingsSaved'));
  };

  const fieldStyle = {
    width: '100%', padding: '0.6rem 0.75rem', border: '1px solid var(--border-color)',
    borderRadius: '0.5rem', fontSize: '0.95rem', background: 'var(--bg-primary)',
    color: 'var(--text-primary)', boxSizing: 'border-box',
  };
  const labelStyle = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.375rem' };

  return (
    <div className="page-container">
      <h1 className="page-title">{t('settingsTitle')}</h1>

      {/* Language */}
      <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
          <Globe size={18} style={{ color: 'var(--text-secondary)' }} />
          <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)' }}>{t('language')}</h3>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          {[{ code: 'es', label: '🇲🇽  Español' }, { code: 'en', label: '🇺🇸  English' }].map(({ code, label }) => (
            <button key={code} onClick={() => updateLang(code)}
              style={{ flex: 1, padding: '0.75rem', border: `2px solid ${lang === code ? '#1a472a' : 'var(--border-color)'}`, borderRadius: '0.625rem', background: lang === code ? 'rgba(26,71,42,0.08)' : 'var(--bg-tertiary)', color: lang === code ? '#1a472a' : 'var(--text-secondary)', fontWeight: 700, fontSize: '0.92rem', cursor: 'pointer' }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Exchange Rates */}
      <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
          <DollarSign size={18} style={{ color: 'var(--text-secondary)' }} />
          <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)' }}>{t('exchangeRates')}</h3>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem', marginBottom: '1.25rem' }}>
          <div>
            <label style={labelStyle}>1 USD → MXN</label>
            <input type="number" step="0.01" value={usdRate} onChange={e => setUsdRate(e.target.value)} style={{ ...fieldStyle, maxWidth: 180 }} />
          </div>
          <div>
            <label style={labelStyle}>1 EUR → MXN</label>
            <input type="number" step="0.01" value={eurRate} onChange={e => setEurRate(e.target.value)} style={{ ...fieldStyle, maxWidth: 180 }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.625rem' }}>
          <button onClick={fetchRates} disabled={fetching}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1rem', border: '1px solid var(--border-color)', borderRadius: '0.5rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', cursor: fetching ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.85rem', opacity: fetching ? 0.6 : 1 }}>
            <RefreshCw size={14} style={{ animation: fetching ? 'spin 1s linear infinite' : 'none' }} />
            {t('fetchAutomatic')}
          </button>
          <button onClick={handleSaveRates}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1rem', border: 'none', borderRadius: '0.5rem', background: '#1a472a', color: 'white', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
            <Save size={14} />
            {t('save')}
          </button>
        </div>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-light)', marginTop: '0.75rem' }}>
          {lang === 'en' ? 'Exchange rates provided by Frankfurter API (ECB data)' : 'Tipos de cambio provistos por Frankfurter API (datos del BCE)'}
        </div>
      </div>

      {/* User Management */}
      <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
          <Users size={18} style={{ color: 'var(--text-secondary)' }} />
          <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)' }}>Usuarios y Acceso</h3>
        </div>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0 0 1rem' }}>
          Solo los usuarios registrados aquí pueden acceder a la aplicación. Al agregar un usuario, copia el link de invitación y compártelo.
        </p>

        {/* Add user row */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* Smart email input with autocomplete */}
          <div style={{ flex: 1, minWidth: '180px', position: 'relative' }}>
            <input
              ref={inputRef}
              type="text"
              placeholder="nombre o email..."
              value={newEmail}
              onChange={e => handleEmailChange(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') { setShowSugg(false); handleAddUser(); }
                if (e.key === 'Escape') { setShowSugg(false); }
              }}
              onBlur={() => setTimeout(() => setShowSugg(false), 150)}
              onFocus={() => suggestions.length > 0 && setShowSugg(true)}
              style={{ ...fieldStyle, width: '100%' }}
            />
            {/* Dropdown */}
            {showSugg && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', boxShadow: '0 4px 16px rgba(0,0,0,0.15)', marginTop: '0.25rem', overflow: 'hidden' }}>
                {suggLoading && (
                  <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Buscando...</div>
                )}
                {!suggLoading && suggestions.map((s, i) => (
                  <div key={i} onMouseDown={() => handleSelectSugg(s)}
                    style={{ padding: '0.5rem 0.875rem', cursor: 'pointer', borderBottom: i < suggestions.length-1 ? '1px solid var(--border-color)' : 'none', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#1a472a22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, color: '#1a472a', flexShrink: 0 }}>
                      {(s.name || s.email).charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)' }}>{s.name || s.email.split('@')[0]}</div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{s.email}{s.existing ? ' · ya registrado' : ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <select value={newRole} onChange={e => setNewRole(e.target.value)}
            style={{ ...fieldStyle, width: '140px', flex: '0 0 140px' }}>
            <option value="Admin">Admin</option>
            <option value="Collaborative">Collaborative</option>
            <option value="Reader">Reader</option>
          </select>
          <button onClick={handleAddUser} disabled={savingUser || !newEmail.trim()}
            style={{ padding: '0.6rem 1rem', border: 'none', borderRadius: '0.5rem', background: '#1a472a', color: 'white', cursor: (!newEmail.trim() || savingUser) ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.85rem', opacity: (!newEmail.trim() || savingUser) ? 0.6 : 1 }}>
            {savingUser ? '...' : 'Agregar'}
          </button>
        </div>

        {/* User list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
          {usersData.length === 0 && (
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem', padding: '1rem 0' }}>Cargando usuarios...</div>
          )}
          {usersData.map(u => (
            <div key={u.id} style={{ border: '1px solid var(--border-color)', borderRadius: '0.625rem', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.625rem 0.875rem', background: 'var(--bg-tertiary)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>{u.displayName || u.email}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{u.email}</div>
                </div>
                {/* Role buttons */}
                <div style={{ display: 'flex', gap: '0.3rem' }}>
                  {['Admin','Collaborative','Reader'].map(r => (
                    <button key={r} onClick={() => handleUpdateRole(u.id, r)}
                      style={{ padding: '0.2rem 0.5rem', border: `2px solid ${(u.role||'Reader')===r ? ROLE_COLORS[r] : 'var(--border-color)'}`, borderRadius: '0.35rem', background: (u.role||'Reader')===r ? `${ROLE_COLORS[r]}18` : 'transparent', color: ROLE_COLORS[r], fontWeight: 700, cursor: 'pointer', fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
                      {r}
                    </button>
                  ))}
                </div>
                {/* Copy invite */}
                <button onClick={() => handleCopyInvite(u.id)} title="Copiar invitación"
                  style={{ padding: '0.3rem', border: '1px solid var(--border-color)', borderRadius: '0.35rem', background: 'var(--bg-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                  {copiedId === u.id ? <Check size={14} color="#10b981" /> : <Copy size={14} color="var(--text-secondary)" />}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Permissions matrix */}
        {usersData.length > 0 && (
          <>
            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.625rem' }}>
              Acceso a módulos
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 0.75rem' }}>
              Determina qué módulos aparecen en la navegación. Admin siempre tiene acceso total. Collaborative puede editar; Reader solo puede ver.
            </p>
            <div style={{ overflowX: 'auto', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '700px', fontSize: '0.72rem' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-tertiary)' }}>
                    <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border-color)', fontWeight: 700, color: 'var(--text-secondary)', whiteSpace: 'nowrap', position: 'sticky', left: 0, background: 'var(--bg-tertiary)', zIndex: 1, minWidth: '120px' }}>
                      Usuario
                    </th>
                    {MODULES.map(m => (
                      <th key={m} style={{ textAlign: 'center', padding: '0.4rem 0.3rem', borderBottom: '1px solid var(--border-color)', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap', minWidth: '70px' }}>
                        {MODULE_LABELS[m]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {usersData.map((u, ui) => (
                    <tr key={u.id} style={{ borderBottom: ui < usersData.length-1 ? '1px solid var(--border-color)' : 'none', background: ui % 2 === 0 ? 'var(--bg-secondary)' : 'var(--bg-primary)' }}>
                      <td style={{ padding: '0.45rem 0.75rem', position: 'sticky', left: 0, background: ui % 2 === 0 ? 'var(--bg-secondary)' : 'var(--bg-primary)', zIndex: 1, whiteSpace: 'nowrap' }}>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.75rem' }}>{u.displayName || u.email.split('@')[0]}</div>
                        <div style={{ color: ROLE_COLORS[u.role] || '#6b7280', fontSize: '0.65rem', fontWeight: 700 }}>{u.role || 'Reader'}</div>
                      </td>
                      {MODULES.map(m => {
                        const isAdmin = u.role === 'Admin';
                        // Normalize: legacy { read, write } treated as true; boolean used directly
                        const raw = u.permissions?.[m];
                        const hasAccess = isAdmin || (typeof raw === 'object' ? true : raw !== false);
                        return (
                          <td key={m} style={{ textAlign: 'center', padding: '0.4rem 0.3rem' }}>
                            <button
                              onClick={() => !isAdmin && handleToggleAccess(u.id, m)}
                              title={isAdmin ? 'Admin: acceso total' : (hasAccess ? 'Quitar acceso' : 'Dar acceso')}
                              style={{
                                width: 24, height: 24, borderRadius: '0.3rem', border: `1px solid ${hasAccess ? '#10b981' : 'var(--border-color)'}`,
                                background: hasAccess ? 'rgba(16,185,129,0.15)' : 'transparent',
                                cursor: isAdmin ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.65rem', color: hasAccess ? '#10b981' : 'var(--text-light)', fontWeight: 700,
                              }}>
                              {hasAccess ? '✓' : ''}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
